import React, { Suspense, useEffect } from 'react'
import { inject, observer } from 'mobx-react'
import { useTranslation } from 'react-i18next'
import useTitle from 'hooks/useTitle'
import LicenceInfo from './LicenceInfo'
import QuickActions from './QuickActions'
import FiscalCalendarWidget from './FiscalCalendarWidget'
import FiscalAlertsBanner from './FiscalAlertsBanner'
import LicenseExpiryBanner from './LicenseExpiryBanner'
import LoadingSpinner from 'Components/LoadingSpinner'
import DashboardKpis from './DashboardKpis'
import { formatUtcDateTime } from 'Utilities/dateTime'
import './Dashboard.scss'

const DashboardCharts = React.lazy(() => import('./DashboardCharts'))

const Dashboard = ({ dashboardStore, companyStore }) => {
    const { t } = useTranslation()
    useTitle('dashboard')

    useEffect(() => {
        dashboardStore.fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const updatedAt = dashboardStore.lastUpdatedAt
        ? formatUtcDateTime(dashboardStore.lastUpdatedAt)
        : null

    return (
        <main className="dashboard-page">
            <header className="dashboard-hero">
                <div className="dashboard-hero__content">
                    <span className="dashboard-hero__eyebrow">
                        {t('dashboard_control_center', { defaultValue: 'Centro de control fiscal' })}
                    </span>
                    <h1>{t('dashboard_welcome', { defaultValue: 'Resumen de su empresa' })}</h1>
                    <p>
                        {companyStore.selectedCompany?.BusinessName}
                        {' · '}
                        {t('dashboard_monthly_context', { defaultValue: 'Indicadores actualizados del mes en curso' })}
                    </p>
                </div>
                <button
                    type="button"
                    className="dashboard-refresh"
                    onClick={() => dashboardStore.fetchData()}
                    disabled={dashboardStore.isLoading}
                >
                    <i className={`fas fa-sync-alt ${dashboardStore.isLoading ? 'fa-spin' : ''}`} aria-hidden="true" />
                    <span>
                        {dashboardStore.isLoading
                            ? t('dashboard_updating', { defaultValue: 'Actualizando...' })
                            : t('refresh', { defaultValue: 'Actualizar' })}
                    </span>
                    {updatedAt && <small>{t('dashboard_updated_at', { defaultValue: 'Última actualización' })}: {updatedAt}</small>}
                </button>
            </header>

            <QuickActions />

            <section className="dashboard-alerts" aria-label={t('dashboard_alerts', { defaultValue: 'Alertas importantes' })}>
                <LicenseExpiryBanner />
                <FiscalAlertsBanner />
            </section>

            {dashboardStore.isLoading && !dashboardStore.data && (
                <div className="dashboard-loading"><LoadingSpinner /></div>
            )}

            {dashboardStore.error && !dashboardStore.data && (
                <div className="dashboard-error" role="alert">
                    <i className="fas fa-exclamation-triangle" aria-hidden="true" />
                    <div>
                        <strong>{t('error_loading_data', { defaultValue: 'No se pudieron cargar los datos' })}</strong>
                        <p>{t('dashboard_retry_hint', { defaultValue: 'Verifique la conexión e intente actualizar nuevamente.' })}</p>
                    </div>
                </div>
            )}

            {dashboardStore.data && (
                <>
                    <DashboardKpis data={dashboardStore.data} />
                    <Suspense fallback={<div className="dashboard-chart-skeleton" aria-hidden="true" />}>
                        <DashboardCharts trend={dashboardStore.data.MonthlyTrend || []} />
                    </Suspense>
                </>
            )}

            <section className="dashboard-secondary-grid">
                <div className="dashboard-secondary-grid__calendar">
                    <FiscalCalendarWidget />
                </div>
                <div className="dashboard-secondary-grid__license">
                    <LicenceInfo />
                </div>
            </section>
        </main>
    )
}

export default inject('dashboardStore', 'companyStore')(observer(Dashboard))